/** Describes a page section: its identity, display label, visibility, order, and lazy-loading config. Each entry maps to a rendered section component. */
export type SectionDef = {
  /** Unique section identifier (kebab-case), used for nav anchors and debug keys */
  id: string
  /** Display label for nav links */
  label: string
  /** Whether this section renders */
  enabled: boolean
  /** Display order in the main content flow */
  order: number
  /** true = React.lazy, false = direct import */
  lazy: boolean
  /** Min-height for Suspense placeholder */
  placeholderMinHeight?: string
  /** Title for the placeholder shimmer */
  placeholderTitle?: string
}

/** All page sections in display order. Controls which sections render, their labels, and lazy-loading behavior. */
export const sectionsConfig: SectionDef[] = [
  { id: 'hero',         label: 'Home',       enabled: true, order: 0,  lazy: false },
  { id: 'about',        label: 'About',      enabled: true, order: 1,  lazy: false },
  { id: 'experience',   label: 'Experience', enabled: true, order: 2,  lazy: false },
  { id: 'achievements', label: 'Milestones', enabled: true, order: 3,  lazy: false },
  { id: 'projects',     label: 'Projects',   enabled: true, order: 4,  lazy: true, placeholderMinHeight: '55vh', placeholderTitle: 'Featured Projects' },
  { id: 'side-projects', label: 'Side Projects', enabled: true, order: 5, lazy: true, placeholderMinHeight: '55vh', placeholderTitle: 'Side Projects & Open Source' },
  { id: 'archive',      label: 'Archive',    enabled: true, order: 6,  lazy: true, placeholderMinHeight: '30vh', placeholderTitle: 'Archive' },
  { id: 'education',    label: 'Education',  enabled: true, order: 7,  lazy: true, placeholderMinHeight: '50vh', placeholderTitle: 'Education & Certifications' },
  { id: 'recommendations', label: 'Endorsements', enabled: true, order: 8, lazy: true, placeholderMinHeight: '45vh', placeholderTitle: 'Professional Endorsements' },
  { id: 'resume',       label: 'Resume',     enabled: true, order: 9,  lazy: true, placeholderMinHeight: '30vh', placeholderTitle: 'RESUME' },
  { id: 'faq',          label: 'FAQ',        enabled: true, order: 10, lazy: true, placeholderMinHeight: '40vh', placeholderTitle: 'Frequently Asked Questions' },
  { id: 'footer',       label: 'Footer',     enabled: true, order: 99, lazy: false },
]

/** Returns all non-footer sections that are enabled, filtered by optional debug overrides and sorted by order or a custom order override. */
export function getActiveSections(
  overrides?: Record<string, boolean>,
  orderOverride?: string[] | null,
): SectionDef[] {
  const result = sectionsConfig.filter((s) => {
    if (s.id === 'footer') return false
    if (overrides) {
      // .toUpperCase() alone leaves hyphens as hyphens, so a kebab-case id like 'side-projects'
      // would look up 'SIDE-PROJECTS' and never match the underscore-cased debug key
      // (SIDE_PROJECTS), silently defaulting to enabled regardless of the flag. Normalize first.
      const key = s.id.replace(/-/g, '_').toUpperCase()
      return overrides[key] !== false
    }
    return s.enabled
  })

  if (orderOverride && orderOverride.length > 0) {
    const orderMap = new Map(orderOverride.map((id, i) => [id, i]))
    result.sort((a, b) => {
      const ai = orderMap.get(a.id)
      const bi = orderMap.get(b.id)
      if (ai !== undefined && bi !== undefined) return ai - bi
      if (ai !== undefined) return -1
      if (bi !== undefined) return 1
      return a.order - b.order
    })
  } else {
    result.sort((a, b) => a.order - b.order)
  }

  return result
}

/** Checks whether the footer section should render. Respects debug overrides first, then falls back to the static config. */
export function getFooterEnabled(overrides?: Record<string, boolean>): boolean {
  if (overrides) {
    return overrides['FOOTER'] !== false
  }
  const footer = sectionsConfig.find((s) => s.id === 'footer')
  return footer ? footer.enabled : true
}

/** Returns sections suitable for the navigation bar (excludes hero and footer). Filters by enabled/override state and sorts by order or custom order override. */
export function getNavSections(
  overrides?: Record<string, boolean>,
  orderOverride?: string[] | null,
): SectionDef[] {
  const result = sectionsConfig.filter((s) => {
    if (['hero', 'faq', 'footer'].includes(s.id)) return false
    if (overrides) {
      const key = s.id.replace(/-/g, '_').toUpperCase()
      return overrides[key] !== false
    }
    return s.enabled
  })

  if (orderOverride && orderOverride.length > 0) {
    const orderMap = new Map(orderOverride.map((id, i) => [id, i]))
    result.sort((a, b) => {
      const ai = orderMap.get(a.id)
      const bi = orderMap.get(b.id)
      if (ai !== undefined && bi !== undefined) return ai - bi
      if (ai !== undefined) return -1
      if (bi !== undefined) return 1
      return a.order - b.order
    })
  } else {
    result.sort((a, b) => a.order - b.order)
  }

  return result
}
